/**
 * The caption on the reel picture. Drag it; the page stores the new place.
 */

import { useRef, useState } from 'react';

interface ReelCaptionDragProps {
  text: string;
  /** 0–1 across the picture. Center of the words. */
  x: number;
  /** 0–1 down the picture. Bottom of the words. */
  y: number;
  onMove: (x: number, y: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Words you can grab on the 9:16 frame. */
export default function ReelCaptionDrag({ text, x, y, onMove }: ReelCaptionDragProps): JSX.Element {
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const origin = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const shown = drag || { x, y };

  const place = (clientX: number, clientY: number, frame: DOMRect): { x: number; y: number } | null => {
    const start = origin.current;
    if (!start || frame.width < 1 || frame.height < 1) return null;
    return {
      x: clamp(start.x + (clientX - start.px) / frame.width, 0.08, 0.92),
      y: clamp(start.y + (clientY - start.py) / frame.height, 0.08, 0.96),
    };
  };

  return (
    <div
      className={`vt-reel-caption-drag${drag ? ' dragging' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Drag caption"
      title="Drag to move"
      style={{ left: `${shown.x * 100}%`, top: `${shown.y * 100}%` }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const frame = event.currentTarget.parentElement?.getBoundingClientRect();
        if (!frame) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        origin.current = { px: event.clientX, py: event.clientY, x, y };
        setDrag({ x, y });
      }}
      onPointerMove={(event) => {
        const frame = event.currentTarget.parentElement?.getBoundingClientRect();
        if (!frame) return;
        const next = place(event.clientX, event.clientY, frame);
        if (next) setDrag(next);
      }}
      onPointerUp={(event) => {
        const frame = event.currentTarget.parentElement?.getBoundingClientRect();
        const next = frame ? place(event.clientX, event.clientY, frame) : null;
        origin.current = null;
        setDrag(null);
        if (next && (Math.abs(next.x - x) > 0.002 || Math.abs(next.y - y) > 0.002)) onMove(next.x, next.y);
      }}
      onPointerCancel={() => {
        origin.current = null;
        setDrag(null);
      }}
    >
      {text}
    </div>
  );
}
